import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../../common/CountryMarketConfig';
import { Ingredient } from '../../common/Ingredients/types';
import { useCountryMarketState } from '../../controllers/CountryMarketController/CountryMarketState';
import { useCountryMarketHelpers } from '../../hooks/useCountryMarketHelpers';
import IngredientRecommendationPlaceholderImage from '../../images/ingredient-recommendation-placeholder.png';
import { isKeyInProductApplications } from '../../lib/briefing';
import { useBriefingState } from '../Briefing/BriefingState';
import { useIngredientsState } from '../Briefing/IngredientsState';
import {
  Recommendation,
  RecommendationsSection,
} from './RecommendationsSection';

// This is needed to pass the Ingredients to RecommendationsSection,
// which only handles Recommendations
export const transformIngredientToRecommendation = (
  ingredient: Ingredient,
  language: Language,
  defaultLanguage: Language,
): Recommendation => ({
  ...ingredient,
  url:
    ingredient.ingredient_url[language] ||
    ingredient.ingredient_url[defaultLanguage],
});

type IngredientRecommendationsProps = {
  initialLimit: number;
  reportRecommendationsExist?: (exists: boolean) => void;
};

export const IngredientRecommendations: React.FC<
  IngredientRecommendationsProps
> = ({
  initialLimit,
  reportRecommendationsExist,
}: IngredientRecommendationsProps) => {
  const { t, i18n } = useTranslation();
  const [
    {
      productApplications,
      requirementsMust,
      requirementsCan,
      azelisRawMaterials,
    },
  ] = useBriefingState();

  const [{ countryMarketConfig }] = useCountryMarketState();

  const { getValidLanguageByUserLanguage, getDefaultLanguage } =
    useCountryMarketHelpers();

  const [ingredientsState] = useIngredientsState();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);

  /**
   * Calculates a fitness score for any ingredient with the
   * given restrictions and requirements of the user.
   *
   * The *smaller* the score, the *better* it fits!
   *
   * `0` means, the ingredient doesn't fit at all,
   * negative values are good.
   */
  const calculateGrade = useCallback(
    (ingredient: Ingredient): number =>
      -[
        // Grade ingredient based on how many requirementsMust they meet
        requirementsMust?.filter(({ value }) =>
          ingredient.marketing_claims.includes(value.toString()),
        ),
        // Grade ingredient based on how many requirementsCan they meet
        requirementsCan?.filter(({ value }) =>
          ingredient.marketing_claims.includes(value.toString()),
        ),
      ]
        .flat()
        .filter((gradeItem) => !!gradeItem).length,
    [requirementsMust, requirementsCan],
  );

  useEffect(() => {
    if (!countryMarketConfig) return;

    const countryMarket = countryMarketConfig.customerPortalMarketSegmentId;
    const ingredients = ingredientsState[countryMarket]?.ingredients;

    const language = getValidLanguageByUserLanguage(
      countryMarketConfig,
      i18n.language as Language,
    );

    if (!ingredients?.length) return;

    const processedIngredients = ingredients
      .filter((ingredient) => {
        const matchesAtLeastOneSelectedApplication =
          ingredient.applications?.some((ingredientApplication) =>
            isKeyInProductApplications(
              ingredientApplication,
              productApplications,
            ),
          );

        const matchesSelectedAzelisRawMaterial = azelisRawMaterials?.some(
          ({ value }) => value === ingredient.itemNumber,
        );

        // Only include if the ingredient:
        // - matches a selected application &&
        // - isn't contained in selected Azelis Raw Materials
        return (
          matchesAtLeastOneSelectedApplication &&
          !matchesSelectedAzelisRawMaterial
        );
      })
      .map((ingredient) => ({ grade: calculateGrade(ingredient), ingredient }))
      .sort((a, b) => a.grade - b.grade)
      .map(({ ingredient }) =>
        transformIngredientToRecommendation(
          ingredient,
          language,
          getDefaultLanguage(countryMarketConfig),
        ),
      );

    setRecommendations(processedIngredients);
  }, [
    azelisRawMaterials,
    calculateGrade,
    countryMarketConfig,
    getDefaultLanguage,
    getValidLanguageByUserLanguage,
    i18n.language,
    ingredientsState,
    productApplications,
  ]);

  return (
    <RecommendationsSection
      initialLimit={initialLimit}
      recommendations={recommendations}
      recommendationType={'ingredient'}
      heading={t('topics:ingredients:name')}
      placeholderImage={IngredientRecommendationPlaceholderImage}
      reportRecommendationsExist={reportRecommendationsExist}
    />
  );
};
